import { apiClient, ApiResponse } from './client';
import type { XamanPayloadResponse } from '../types/signing';

const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

export interface PinnedImage {
  cid: string;
  ipfs_uri: string;
  mime_type: string;
  filename: string;
  size_bytes: number;
}

export interface TaxonCheckResult {
  available: boolean;
  existingCollection?: { id: string; name: string; slug: string | null };
}

export interface NftTraitInput {
  trait_type: string;
  value: string;
}

export interface NewCollectionInput {
  name: string;
  description?: string;
  slug?: string;
  taxon: number;
  royalty_percentage: number;
  royalty_address?: string;
  image_cid?: string;
  banner_cid?: string;
  external_url?: string;
  project_id?: string | null;
}

export interface PrepareMintInput {
  collection: { id: string } | { newCollection: NewCollectionInput };
  nft: {
    name: string;
    description?: string;
    primary_image: { cid: string; mime_type: string };
    /** The single main rich asset (video / 3D model) → animation_url. */
    animation?: { cid: string; mime_type: string };
    /** Extra mixed-media files (images, videos, 3D models) → properties.files. */
    alternative_images?: Array<{ cid: string; mime_type: string }>;
    traits?: NftTraitInput[];
    external_url?: string;
  };
  transfer_fee_override?: number;
  flags?: {
    transferable?: boolean;
    burnable?: boolean;
    only_xrp?: boolean;
    mutable?: boolean;
  };
}

export interface PrepareMintResponse {
  signingIntent?: { intentId: string; provider: string; txJson?: Record<string, unknown> };
  payload?: XamanPayloadResponse;
  metadata: { cid: string; uri: string };
  collection: { id: string; taxon: number };
  txjson: Record<string, unknown>;
}

export async function uploadMintImages(files: File[]): Promise<PinnedImage[]> {
  const formData = new FormData();
  for (const f of files) {
    formData.append('files', f, f.name);
  }

  const token = localStorage.getItem('xrpl_marketplace_token');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}/mint/upload-images`, {
    method: 'POST',
    body: formData,
    headers,
    credentials: 'include',
  });

  const result = (await res.json()) as ApiResponse<{ images: PinnedImage[] }>;
  if (!res.ok || !result.success || !result.data) {
    throw new Error(result.error || result.message || 'Failed to pin images');
  }
  return result.data.images;
}

export async function checkTaxonAvailable(taxon: number): Promise<TaxonCheckResult> {
  const res = await apiClient.get<ApiResponse<TaxonCheckResult>>(`/mint/taxon-check?taxon=${taxon}`);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to check taxon availability');
  }
  return res.data;
}

export async function prepareMint(input: PrepareMintInput): Promise<PrepareMintResponse> {
  const res = await apiClient.post<ApiResponse<PrepareMintResponse>>('/mint/prepare', input);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to prepare mint');
  }
  return res.data;
}

export interface MintStatusResponse {
  validated: boolean;
  tokenId: string | null;
  indexed: boolean;
  enriched: boolean;
}

export async function getMintStatus(txHash: string): Promise<MintStatusResponse> {
  const res = await apiClient.get<ApiResponse<MintStatusResponse>>(`/mint/status/${txHash}`);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to fetch mint status');
  }
  return res.data;
}
